import { LoadingSpinner } from "@/components/common/LoadingSpinner/LoadingSpinner"
import { LogoMark } from "@/components/common/Logo/LogoMark"
import type { PendingConfirmation } from "@/contexts/AgentChatContext"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { AnimatePresence } from "motion/react"
import * as m from "motion/react-m"
import React, { useEffect, useRef } from "react"

interface AgentConfirmationBarProps {
  confirmation: PendingConfirmation | null
  onResolve: (confirmed: boolean) => void
}

/**
 * The blocking Confirm / Cancel affordance, rendered as part of the composer
 * rather than inline in the message thread.
 *
 * A pending confirmation is genuinely modal state — `requestConfirmation`
 * suspends a tool call on a promise until the user decides, and only one can
 * exist at a time. Inline in the thread the composer stayed live underneath
 * it, so a message sent meanwhile got its reply interleaved with the resumed
 * tool call's output and the thread ordering broke.
 *
 * Fuses to the top edge of the composer the same way ChatMessageQueue does
 * (-mb-px, no bottom border, top-only radius) so it reads as the input
 * expanding upward rather than a card floating above it.
 */
export const AgentConfirmationBar: React.FC<AgentConfirmationBarProps> = ({
  confirmation,
  onResolve,
}) => {
  const { translate } = useTranslation()
  const confirmButtonRef = useRef<HTMLButtonElement>(null)
  const isPending = confirmation?.status === "pending"

  // Focus the primary action as the bar appears: the composer it grew out of
  // is disabled at the same moment, so without this focus is left sitting on
  // a dead control with nothing keyboard-reachable nearby.
  useEffect(() => {
    if (isPending) {
      confirmButtonRef.current?.focus()
    }
  }, [isPending])

  // Escape cancels. Bound to the document, not the bar, because focus can be
  // anywhere on the page — the user may have been mid-scroll when the agent
  // asked.
  useEffect(() => {
    if (!isPending) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault()
        onResolve(false)
      }
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [isPending, onResolve])

  return (
    <AnimatePresence initial={false}>
      {confirmation && (
        <m.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="mx-auto -mb-px max-w-5xl overflow-hidden"
        >
          <div
            role="alertdialog"
            aria-modal="false"
            aria-label={translate("AI_CHAT/CONFIRMATION_PENDING_LABEL")}
            data-testid="agent-confirmation-bar"
            className="rounded-t-lg border-x border-b-0 border-t border-primary/40 bg-grey1/80 px-M py-S shadow-[0_-12px_36px_hsl(var(--primary)/0.08)] backdrop-blur-sm"
          >
            <div className="flex items-start gap-2">
              <LogoMark className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-medium uppercase tracking-wide text-primary">
                  {translate("AI_CHAT/CONFIRMATION_PENDING_LABEL")}
                </p>
                <p className="mt-0.5 text-sm font-medium text-foreground">
                  {confirmation.title}
                </p>
                {/* Several call sites in toolExecutor.ts build multi-line
                    details with "\n\n" — collapsing them makes the summary of
                    what is about to be written unreadable. */}
                <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">
                  {confirmation.detail}
                </p>

                {confirmation.status === "confirmed" ? (
                  // Confirmed but not yet resolved — the tool call this
                  // unblocked is still in flight. The composer stays disabled
                  // for exactly this window, so re-enabling here would reopen
                  // the ordering hole the bar exists to close.
                  <div className="mt-S flex items-center gap-2 text-sm text-muted-foreground">
                    <LoadingSpinner size={14} />
                    <span>{translate("AI_CHAT/TOOL_STATUS/GENERIC")}</span>
                  </div>
                ) : (
                  <div className="mt-S flex gap-2">
                    <button
                      ref={confirmButtonRef}
                      type="button"
                      onClick={() => onResolve(true)}
                      className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-yellow2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                    >
                      {translate("AI_CHAT/CONFIRM")}
                    </button>
                    <button
                      type="button"
                      onClick={() => onResolve(false)}
                      className="rounded-md border border-border/60 px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-grey4"
                    >
                      {translate("AI_CHAT/CANCEL")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </m.div>
      )}
    </AnimatePresence>
  )
}
